import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import type { ParseKeys } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  X,
  Plus,
  Users,
  Loader2,
  Search,
  Briefcase,
  MapPin,
  User,
  Building2,
  Globe,
  Tag,
  AlertCircle,
  Sparkles,
} from '@digital-twins/design-system/icons'
import { cn } from '@/lib/utils'
import { populationsApi } from '@/lib/api'
import {
  cleanLabel,
  humanizeColumnName,
  resolveDisplayLabel,
  extractItemLabel,
  displaySegmentValueLabel,
  groupColumnsBySharedStem,
  splitStemAndItem,
} from '@/lib/utils/column-labels'
import { includesQuery, isDisplayableCode } from '@/lib/utils/filter-search'
import { buildSegmentFilterQuestions } from '@/lib/utils/segment-filter-questions'
import type { SegmentFilterColumnInput } from '@/lib/utils/segment-filter-questions'
import { AiFilterCondition } from '@/types/components/audience-filter-modal'
import { InlineAskAiFilter } from './InlineAskAiFilter'
import type { ColumnGroupMetadata, SegmentColumnMetadata } from '@/types/routes/tests'
import type { PopulationFilter } from '@/types/routes/test-results'
import type { AudienceFilterModalProps, ColumnMetadataResult } from '@/types/components/audience-filter-modal'
import {
  SINGLE_ONLY_FILTER_CATEGORIES,
  buildFilterFromSelections,
  filterGroupEntry,
  isSelectedNotSelectedGroup,
  materializeGroupMembers,
  multiselectGroupConstrainedOptions,
  resolvePositiveValue,
  selectionsFromAiChoices,
  type FilterGroupEntry,
  type FilterGroupOption,
  type FilterGroupRowSelection,
  type AudienceFilterLabel,
  type AskAiFilterChoice,
  type SelectedSegment,
} from '@digital-twins/shared-types'
import { choicesFromInitialFilter } from './initialFilterChoices'

const MIN_PERSONAS = 30
const PREVIEW_DEBOUNCE_MS = 300
const COLUMN_CACHE_TTL_MS = 60_000

interface CacheEntry {
  data: ColumnMetadataResult;
  expiresAt: number;
}

const columnMetadataCache = new Map<string, CacheEntry>();

function getCached(key: string): ColumnMetadataResult | null {
  const entry = columnMetadataCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    columnMetadataCache.delete(key);
    return null;
  }
  return entry.data;
}

function setCached(key: string, data: ColumnMetadataResult): void {
  columnMetadataCache.set(key, { data, expiresAt: Date.now() + COLUMN_CACHE_TTL_MS });
}

type ColumnMeta = SegmentColumnMetadata

/**
 * One option (member column) within a group — the 2nd dimension, e.g.
 * "Salesforce" under "Which … vendors?". Its values are the shared entry's
 * (`filterGroupEntry`); the label is display only.
 */
interface GroupOption extends FilterGroupOption {
  /** Clean item label, e.g. "Salesforce". */
  label: string
}

/**
 * A whole question group (binary or multiselect): the shared filter entry
 * (`filterGroupEntry`, which the filter builder reads) plus its display.
 */
export interface GroupEntry extends FilterGroupEntry {
  /** Human-readable label derived from rawLabel or groupId (may be truncated for display). */
  label: string
  /** Full, untruncated label for hover tooltips. */
  fullLabel: string
  options?: Array<GroupOption>
  category: string
}

type CategorySection = {
  category: string
  columns: Array<ColumnMeta>
  groups: Array<GroupEntry>
}

/**
 * Convert a raw group label (often a column code or question text) into a
 * short, readable display string.
 *
 * Uses the same `cleanLabel` + `humanizeColumnName` pipeline that individual
 * columns use, so group labels are consistent with field labels across the UI.
 */
/** Full, cleaned group label (no length cap) — used for hover tooltips. */
function cleanGroupLabel(raw: string): string {
  if (raw.includes(' ')) {
    // Natural-language question text — clean it with the shared utility
    // (strips code prefixes, descriptor suffixes, etc.).
    return cleanLabel(raw) || raw
  }
  // Code-style label (underscores, e.g. "q_brand_awareness_0") — delegate to
  // the existing humanizer which handles camelCase, underscores, and prefix stripping.
  return humanizeColumnName(raw) || raw
}

function humanizeGroupLabel(raw: string): string {
  const cleaned = cleanGroupLabel(raw)
  return cleaned.length > 60 ? cleaned.slice(0, 57) + '…' : cleaned
}

/**
 * Resolve the question text for a filter group. Backend group ids / rawLabels are
 * often column codes (e.g. "Q4r1"); derive the shared question stem from member
 * column labels when possible — same stem logic as buildSegmentFilterQuestions.
 */
function resolveGroupQuestionLabel(
  group: ColumnGroupMetadata,
  members: Array<ColumnMeta>,
  groupOverrideLabel?: string,
): { label: string; fullLabel: string } {
  if (groupOverrideLabel) {
    return {
      label: humanizeGroupLabel(groupOverrideLabel),
      fullLabel: cleanGroupLabel(groupOverrideLabel),
    }
  }

  const stemCounts = new Map<string, number>()
  let firstMemberFullLabel: string | null = null
  for (const col of members) {
    const full = resolveDisplayLabel(col)
    if (!firstMemberFullLabel) firstMemberFullLabel = full
    const split = splitStemAndItem(full, col.name)
    if (split?.stem) {
      stemCounts.set(split.stem, (stemCounts.get(split.stem) ?? 0) + 1)
    }
  }
  if (stemCounts.size > 0) {
    const bestStem = [...stemCounts.entries()].sort((a, b) => b[1] - a[1])[0][0]
    const stemLabel = cleanGroupLabel(bestStem)
    return {
      label: humanizeGroupLabel(bestStem),
      fullLabel: stemLabel,
    }
  }

  const rawForLabel = group.rawLabel ?? group.groupId
  return {
    label: humanizeGroupLabel(rawForLabel),
    fullLabel: cleanGroupLabel(rawForLabel),
  }
}

const FILTER_OPTION_CHECKBOX_CLASS =
  'h-4 w-4 shrink-0 border-border bg-background data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground'

function filterOptionPillClass(isChecked: boolean, extra?: string): string {
  return cn(
    'flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg transition-colors',
    extra,
    isChecked
      ? 'border border-primary/40 bg-primary/8 dark:border-primary/30 dark:bg-primary/10'
      : 'border border-transparent bg-transparent hover:bg-accent/30 dark:hover:bg-accent/15',
  )
}

/**
 * A label that truncates visually (CSS) but reveals its full text on hover via a
 * styled tooltip. `tooltip` is the full text; `display` overrides the shown text
 * when it differs (e.g. a shortened header), otherwise the full text is shown and
 * CSS handles truncation.
 */
function HoverLabel({
  tooltip,
  display,
  className,
}: {
  tooltip: string
  display?: string
  className?: string
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={className}>{display ?? tooltip}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[calc(100vw-2rem)] break-words">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const CATEGORY_CONFIG: Record<string, { labelKey: ParseKeys<'app'>; icon: React.ReactNode }> = {
  demographics: { labelKey: 'creation.filterCatDemographics', icon: <User className="h-4 w-4" /> },
  geography: { labelKey: 'creation.filterCatGeographic', icon: <Globe className="h-4 w-4" /> },
  location: { labelKey: 'creation.filterCatLocation', icon: <MapPin className="h-4 w-4" /> },
  professional: { labelKey: 'creation.filterCatProfessional', icon: <Briefcase className="h-4 w-4" /> },
  firmographics: { labelKey: 'creation.filterCatFirmographics', icon: <Building2 className="h-4 w-4" /> },
  company: { labelKey: 'creation.filterCatCompany', icon: <Building2 className="h-4 w-4" /> },
  custom: { labelKey: 'creation.filterCatCustom', icon: <Tag className="h-4 w-4" /> },
  other: { labelKey: 'creation.filterCatOther', icon: <Tag className="h-4 w-4" /> },
}

/**
 * Recognized segmentation categories. Columns in these categories are always
 * shown as standalone filters and are never absorbed into an auto-detected
 * battery group, mirroring `buildSegmentFilterQuestions` (the audience filter
 * editor). This keeps the "Filter population" modal and the "Edit audience
 * filters" view in agreement on what counts as a group: only uncategorized
 * ("other") questions form groups; demographics/firmographics/etc. stay single.
 */
const SINGLE_ONLY_CATEGORIES = SINGLE_ONLY_FILTER_CATEGORIES

function resolveFieldLabel(col: ColumnMeta): string {
  const full = resolveDisplayLabel(col)
  return extractItemLabel(full)
}

const QUESTION_SKIP_WORDS = new Set([
  'which', 'of', 'the', 'following', 'best', 'describes', 'describe',
  'your', 'what', 'is', 'are', 'do', 'does', 'how', 'many', 'often',
  'a', 'an', 'in', 'to', 'for', 'with', 'by', 'at', 'from', 'that',
  'this', 'it', 'its', 'and', 'or', 'but', 'on', 'as', 'you', 'have',
  'has', 'been', 'be', 'would', 'could', 'should', 'will', 'most',
  'about', 'any', 'please', 'select', 'choose', 'indicate', 'specify',
])

function toShortLabel(text: string): string {
  const words = text.replace(/[?.,!:;]/g, '').split(/\s+/).filter(Boolean)
  const meaningful = words.filter((w) => !QUESTION_SKIP_WORDS.has(w.toLowerCase()))
  const taken = (meaningful.length > 0 ? meaningful : words).slice(0, 3)
  return taken.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

/** Compact value summary for chips: shows up to 3 values, then "+N". */
function summarizeValues(labels: Array<string>): string {
  if (labels.length <= 3) return labels.join(', ')
  return `${labels.slice(0, 3).join(', ')} +${labels.length - 3}`
}

/**
 * The column chips as the flat `column → values` map `onAiApplyFilters` hands
 * the parent. A group chip has no flat form ("any of its options"); the parent
 * gets it in the filter `onFilterApply` sends.
 */
function columnChipFilters(chips: Array<AiFilterCondition>): Record<string, Array<string>> {
  const filters: Record<string, Array<string>> = {}
  for (const chip of chips) {
    if (!chip.groupId) filters[chip.column] = chip.values
  }
  return filters
}

function initializeSelections(columns: ColumnMeta[]): Record<string, Set<string>> {
  const selections: Record<string, Set<string>> = {}
  for (const col of columns) {
    selections[col.name] = new Set()
  }
  return selections
}

/** Two-row (Options × Values) selection for a group (shared owner). */
export type GroupRowSelection = FilterGroupRowSelection

export function AudienceFilterModal({
  open,
  onOpenChange,
  populationId,
  populationName,
  coverImageUrl,
  prefetchedData,
  showOperationalColumns = false,
  audienceType,
  onFilterApply,
  onContinueWithout,
  onPreviewFilter,
  minPersonas = MIN_PERSONAS,
  onAiApplyFilters,
  activeAiChips,
  audienceTags,
  forStudy,
  initialFilter,
}: AudienceFilterModalProps) {
  const { t } = useTranslation('app')
  const [columns, setColumns] = useState<ColumnMeta[]>([])
  const [groups, setGroups] = useState<Array<ColumnGroupMetadata>>([])
  const [totalRows, setTotalRows] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [selections, setSelections] = useState<Record<string, Set<string>>>({})
  /** Opt-in selections for binary group entries (keyed by groupId). Empty set = no filter. */
  const [groupSelections, setGroupSelections] = useState<Record<string, Set<string>>>({})
  /**
   * Opt-in per-option selections for 3D multiselect groups:
   * groupId → (member column → selected values). Empty = no filter.
   */
  const [groupMemberSelections, setGroupMemberSelections] = useState<
    Record<string, Record<string, Set<string>>>
  >({})
  /**
   * UI-level two-row selection for multiselect groups (groupId → options/values).
   * This is the source of truth the Options/Values rows render from;
   * {@link groupMemberSelections} is kept as a derived mirror so the filter
   * payload and all downstream consumers stay unchanged.
   */
  const [groupRowSelections, setGroupRowSelections] = useState<
    Record<string, GroupRowSelection>
  >({})
  /**
   * Per-group boolean logic for multiselect groups (groupId → 'OR' | 'AND').
   * Absent entry defaults to 'OR' (match ANY selected option). Switching to
   * 'AND' requires ALL selected options to fall in the value set.
   */
  const [groupLogic, setGroupLogic] = useState<Record<string, 'OR' | 'AND'>>({})
  const [matchingCount, setMatchingCount] = useState<number | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [aiChips, setAiChips] = useState<Array<AiFilterCondition>>([])
  const [activeTagIds, setActiveTagIds] = useState<Set<string>>(new Set())
  const [tagError, setTagError] = useState<string | null>(null)
  /** Column names injected synthetically from tag conditions (not in original metadata). */
  const [syntheticColNames, setSyntheticColNames] = useState<Set<string>>(new Set())

  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Normalize audienceTags: old audiences may store sourceColumn as the display
  // label (e.g. "Question text (CODE)") instead of the coded column name.
  // Resolve once so all downstream code sees the canonical column name.
  const normalizedTags = useMemo(() => {
    if (!audienceTags || columns.length === 0) return audienceTags ?? null
    const colByName = new Map(columns.map((c) => [c.name, c]))
    // Build reverse lookup: lowercase display label → column name
    const labelToName = new Map<string, string>()
    for (const c of columns) {
      labelToName.set(resolveDisplayLabel(c).toLowerCase().trim(), c.name)
    }

    let anyResolved = false
    const resolved = audienceTags.map((tag) => {
      const conditions = tag.conditions.map((cond) => {
        if (colByName.has(cond.sourceColumn)) return cond

        // Try extracting coded name from trailing parentheses: "Question text (CODE)"
        const parenMatch = cond.sourceColumn.match(/\(([^)]+)\)\s*$/)
        if (parenMatch) {
          const code = parenMatch[1]
          const match = columns.find(
            (c) => c.name === code || c.name.toLowerCase() === code.toLowerCase(),
          )
          if (match) {
            anyResolved = true
            return { ...cond, sourceColumn: match.name }
          }
        }

        // Try matching by display label
        const srcLabel = cond.sourceColumn.toLowerCase().replace(/\s*\([^)]*\)\s*$/, '').trim()
        const nameByLabel = labelToName.get(srcLabel)
        if (nameByLabel) {
          anyResolved = true
          return { ...cond, sourceColumn: nameByLabel }
        }

        return cond
      })
      return conditions === tag.conditions ? tag : { ...tag, conditions }
    })
    return anyResolved ? resolved : audienceTags
  }, [audienceTags, columns])

  /**
   * Fallback groups derived from two sources when no Argo-generated
   * column_groups.json exists (legacy or recently-created populations):
   *
   * 1. `groupDisplayLabel` — columns that share the same non-null groupDisplayLabel
   *    were explicitly grouped by the user in the Segmentation step. These take
   *    priority and their members are excluded from stem detection.
   * 2. Label-stem heuristic — remaining uncategorized columns are grouped by shared
   *    question stem (the same Phase-0-style logic as AddFilterModal).
   *
   * cached per-respondent across open/close cycles (stable until columns change)
   */
  const stemDerivedGroups = useMemo<Array<ColumnGroupMetadata>>(() => {
    if (columns.length === 0) return []

    // Columns already covered by backend-provided groups — skip them.
    const backendGroupMembers = new Set(groups.flatMap((g) => g.memberColumns))

    const eligibleCols = (showOperationalColumns
      ? columns
      : columns.filter((c) => !c.isOperational)
    ).filter((c) => !backendGroupMembers.has(c.name) && !syntheticColNames.has(c.name))

    if (eligibleCols.length === 0) return []

    // Pass 1: user-named groups (columns sharing the same groupDisplayLabel).
    const labelToMembers = new Map<string, string[]>()
    for (const col of eligibleCols) {
      const label = col.groupDisplayLabel?.trim()
      if (!label) continue
      if (!labelToMembers.has(label)) labelToMembers.set(label, [])
      labelToMembers.get(label)!.push(col.name)
    }
    const labelGroups: Array<ColumnGroupMetadata> = []
    const labelGroupMemberSet = new Set<string>()
    for (const [label, members] of labelToMembers) {
      if (members.length < 2) continue
      for (const m of members) labelGroupMemberSet.add(m)
      labelGroups.push({
        groupId: label,
        rawLabel: label,
        groupType: 'multiselect' as const,
        memberColumns: members,
        responseEncoding: null,
        rawGroupType: null,
        detectionRoute: 'group-display-label',
        source: 'phase0' as const,
      })
    }

    // Pass 2: use the shared buildSegmentFilterQuestions for consistent
    // stem-based grouping on columns not covered by a label group.
    const stemEligible = eligibleCols.filter((c) => !labelGroupMemberSet.has(c.name))
    const inputs: Array<SegmentFilterColumnInput> = stemEligible.map((c) => ({
      sourceColumn: c.name,
      normalizedLabel: resolveDisplayLabel(c),
      originalLabel: resolveDisplayLabel(c),
      category: null,
      sampleValues: [],
    }))
    const questions = buildSegmentFilterQuestions(inputs)
    const stemResult: Array<ColumnGroupMetadata> = questions
      .filter((q) => q.kind === 'group' && q.options.length >= 2)
      .map((q) => ({
        groupId: q.label,
        rawLabel: q.label,
        groupType: 'multiselect' as const,
        memberColumns: q.options.map((o) => o.sourceColumn),
        responseEncoding: null,
        rawGroupType: null,
        detectionRoute: 'stem-fallback',
        source: 'phase0' as const,
      }))

    return [...labelGroups, ...stemResult]
  }, [groups, columns, showOperationalColumns, syntheticColNames])

  /** Backend groups merged with stem-derived groups for uncovered columns. */
  const effectiveGroups = useMemo(
    () => [...groups, ...stemDerivedGroups],
    [groups, stemDerivedGroups],
  )

  /**
   * Effective groups with degenerate self-referential members removed, plus the
   * set of column names that were stripped.
   *
   * Some exports carry a leftover column whose label merely restates the group's
   * own question stem (e.g. "<question>: - <question>") or is empty. It gets
   * bucketed as a group member but holds no real per-option answer; when included
   * it surfaces as a junk option/attribute and can distort matching counts. Such
   * columns are dropped from the group AND hidden from the individual-column list
   * (`degenerateColumnSet`) so they don't appear anywhere in the filter UI.
   */
  const { sanitizedGroups, degenerateColumnSet } = useMemo<{
    sanitizedGroups: Array<ColumnGroupMetadata>
    degenerateColumnSet: Set<string>
  }>(() => {
    const removed = new Set<string>()
    if (effectiveGroups.length === 0) {
      return { sanitizedGroups: effectiveGroups, degenerateColumnSet: removed }
    }
    const colByName = new Map(columns.map((c) => [c.name, c]))
    const normalize = (s: string) =>
      s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
    const sanitized = effectiveGroups.map((g) => {
      // (1) Unconditionally drop members that belong to a recognized segmentation
      // category. Those columns are always individual filters, so an auto-detected
      // battery must never swallow a demographic/firmographic the user selected on
      // its own (e.g. "Annual Revenue" or "Industry"). Stripped names are NOT added
      // to `removed`, so they still surface in the individual-column list.
      let members = g.memberColumns.filter((name) => {
        const col = colByName.get(name)
        if (!col) return true
        return !SINGLE_ONLY_CATEGORIES.has(col.category ?? 'other')
      })

      // (2) Drop degenerate members whose label merely restates the group stem (or
      // is empty), but only when >= 2 real members remain (else keep the group).
      const groupLabelNorm = normalize(cleanGroupLabel(g.rawLabel ?? g.groupId))
      if (groupLabelNorm) {
        const degenerate: Array<string> = []
        const deduped = members.filter((name) => {
          const col = colByName.get(name)
          if (!col) return true
          const itemNorm = normalize(resolveFieldLabel(col))
          const isDegenerate =
            itemNorm.length === 0 || itemNorm === groupLabelNorm
          if (isDegenerate) degenerate.push(name)
          return !isDegenerate
        })
        if (deduped.length !== members.length && deduped.length >= 2) {
          for (const name of degenerate) removed.add(name)
          members = deduped
        }
      }

      return members.length === g.memberColumns.length
        ? g
        : { ...g, memberColumns: members }
    })
    return { sanitizedGroups: sanitized, degenerateColumnSet: removed }
  }, [effectiveGroups, columns])

  /** Set of column names that belong to a group — hidden from the individual column list. */
  const memberColumnSet = useMemo(
    () => new Set(sanitizedGroups.flatMap((g) => g.memberColumns)),
    [sanitizedGroups],
  )

  const filteredColumns = useMemo(() => {
    const base = showOperationalColumns
      ? columns
      : columns.filter((c) => !c.isOperational)
    if (memberColumnSet.size === 0 && degenerateColumnSet.size === 0) return base
    return base.filter(
      (c) => !memberColumnSet.has(c.name) && !degenerateColumnSet.has(c.name),
    )
  }, [columns, showOperationalColumns, memberColumnSet, degenerateColumnSet])

  /** Virtual entries for each effective question group with aggregated unique values. */
  const groupEntries = useMemo<Array<GroupEntry>>(() => {
    if (!sanitizedGroups.length) return []
    return sanitizedGroups.flatMap((g) => {
      // Membership via a Set instead of `g.memberColumns.includes(...)` per
      // column — keeps the same column ordering but makes the lookup O(1),
      // dropping the old O(groups × columns × members) cost.
      const memberSet = new Set(g.memberColumns)
      const members = columns.filter((c) => memberSet.has(c.name))
      if (members.length === 0) return []

      // Values and options: the shared entry the filter builder reads.
      const entry = filterGroupEntry(g, members)
      if (!entry) return []
      const category = members[0]?.category ?? 'other'
      const labelOf = new Map(members.map((col) => [col.name, resolveFieldLabel(col)]))
      const options: Array<GroupOption> | undefined = entry.options?.map((opt) => ({
        ...opt,
        label: labelOf.get(opt.column) ?? opt.column,
      }))

      // Prefer a user alias if any member carries one (set at create time).
      // Reading it off member metadata covers both Argo and stem-fallback groups,
      // and tolerates regrouping since every member carries the same alias.
      const groupOverrideLabel = members
        .map((c) => c.groupDisplayLabel?.trim())
        .find((label): label is string => Boolean(label))

      const { label: groupLabel, fullLabel: groupFullLabel } = resolveGroupQuestionLabel(
        g,
        members,
        groupOverrideLabel,
      )

      return [
        {
          ...entry,
          label: groupLabel,
          fullLabel: groupFullLabel,
          options,
          category,
        },
      ]
    })
  }, [sanitizedGroups, columns])

  const groupEntriesByCategory = useMemo(() => {
    const map = new Map<string, Array<GroupEntry>>()
    for (const g of groupEntries) {
      if (!map.has(g.category)) map.set(g.category, [])
      map.get(g.category)!.push(g)
    }
    return map
  }, [groupEntries])

  const categorizedColumns = useMemo(() => {
    const catMap = new Map<string, ColumnMeta[]>()
    for (const col of filteredColumns) {
      const cat = col.category || 'other'
      if (!catMap.has(cat)) catMap.set(cat, [])
      catMap.get(cat)!.push(col)
    }
    const CATEGORY_ORDER = ['demographics', 'geography', 'other']
    return Array.from(catMap.entries()).sort(([a], [b]) => {
      const ai = CATEGORY_ORDER.indexOf(a)
      const bi = CATEGORY_ORDER.indexOf(b)
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
    })
  }, [filteredColumns])

  /**
   * Merged category sections including both individual columns and group entries.
   * Replaces the old `[string, ColumnMeta[]][]` searchFilteredCategories.
   */
  const searchFilteredCategories = useMemo<Array<CategorySection>>(() => {
      const q = searchQuery.trim()

    // Seed from column categories (preserves sort order from categorizedColumns).
    const sections = new Map<string, CategorySection>()
    for (const [cat, cols] of categorizedColumns) {
      const filteredCols = q
        ? cols.filter((c) => {
            // Match by display label OR by underlying column code (name).
            if (includesQuery(q, resolveFieldLabel(c), c.name)) return true
            return c.uniqueValues.some((v) => {
              const display = displaySegmentValueLabel(c, v)
              return includesQuery(q, v, display)
            })
          })
        : cols
      sections.set(cat, { category: cat, columns: filteredCols, groups: [] })
    }

    // Merge group entries (may introduce new categories not covered by columns).
    for (const [cat, grps] of groupEntriesByCategory) {
      const filteredGrps = q
        ? grps.filter((g) => {
            // Match group label, full label, groupId (code), or any member column
            // code / option label so that typing a variable code like "q3a_salesforce"
            // surfaces the parent group even when the group label doesn't match.
            if (includesQuery(q, g.label, g.fullLabel, g.groupId)) return true
            if (g.memberColumns.some((mc) => includesQuery(q, mc))) return true
            if (
              g.options?.some(
                (o) =>
                  includesQuery(q, o.label, o.column) ||
                  o.uniqueValues.some((v) => includesQuery(q, o.valueLabels?.[v] ?? v)),
              ) ??
              false
            ) return true
            return false
          })
        : grps
      if (!sections.has(cat)) {
        sections.set(cat, { category: cat, columns: [], groups: filteredGrps })
      } else {
        sections.get(cat)!.groups = filteredGrps
      }
    }

    return Array.from(sections.values())
      .filter((s) => s.columns.length > 0 || s.groups.length > 0)
  }, [categorizedColumns, searchQuery, groupEntriesByCategory])

  // An active manual filter exists iff the current selections would actually
  // produce a filter payload. Deriving it from buildFilterFromSelections keeps
  // the "Filter population" button in lockstep with what gets applied (opt-in:
  // nothing selected === no filter; all selected === no filter).
  const hasActiveManualFilter = useMemo(
    () =>
      buildFilterFromSelections(
        selections,
        filteredColumns,
        groupSelections,
        groupEntries,
        groupMemberSelections,
        undefined,
        syntheticColNames,
      ) !== null,
    [selections, filteredColumns, groupSelections, groupEntries, groupMemberSelections, syntheticColNames],
  )

  const [tagSizes, setTagSizes] = useState<Record<string, number>>({})

  useEffect(() => {
    if (!forStudy || !normalizedTags || normalizedTags.length === 0 || loading || filteredColumns.length === 0) return

    let cancelled = false
    const previewFn = onPreviewFilter ?? ((f: PopulationFilter) => populationsApi.previewFilter(populationId, f))

    for (const tag of normalizedTags) {
      // The segment's columns as selections, through the one builder: the
      // same filter its activation applies (C09-1).
      const filter = buildFilterFromSelections(
        Object.fromEntries(tag.conditions.map((c) => [c.sourceColumn, new Set(c.values)])),
        [],
      )
      if (!filter) continue
      previewFn(filter)
        .then((result) => {
          if (cancelled) return
          setTagSizes((prev) => ({ ...prev, [tag.id]: result.matchingCount }))
        })
        .catch(() => {})
    }

    return () => { cancelled = true }
  }, [forStudy, normalizedTags, loading, filteredColumns.length, populationId, onPreviewFilter])

  // Every constraint of the pick in the catalog's labels, by the same rules
  // `buildFilterFromSelections` applies: one pass, read by the chips and by
  // the filter's labelled projection `draft.audience` stores (C09-4).
  const labelledConstraints = useMemo(() => {
    const constraints: Array<{
      field: string
      label: string
      display: string
      tooltip: string
      values: string[]
      projection: AudienceFilterLabel
    }> = []
    const pushColumn = (col: ColumnMeta, selected: Set<string>) => {
      const label = resolveFieldLabel(col)
      const valueLabels = Array.from(selected).map((v) => displaySegmentValueLabel(col, v))
      constraints.push({
        field: col.name,
        label,
        display: `${toShortLabel(label)}: ${summarizeValues(valueLabels)}`,
        tooltip: `${label}: ${valueLabels.join(', ')}`,
        values: Array.from(selected),
        projection: { attribute: resolveDisplayLabel(col), values: valueLabels },
      })
    }
    for (const col of filteredColumns) {
      const selected = selections[col.name]
      if (!selected) continue
      if (selected.size > 0 && (syntheticColNames.has(col.name) || selected.size < col.uniqueValues.length)) {
        pushColumn(col, selected)
      }
    }
    const filteredColumnNames = new Set(filteredColumns.map((c) => c.name))
    for (const [colName, selected] of Object.entries(selections)) {
      if (filteredColumnNames.has(colName)) continue
      if (!selected || selected.size === 0) continue
      const col = columns.find((c) => c.name === colName)
      if (col) {
        pushColumn(col, selected)
        continue
      }
      // A column outside the catalog (a resolved identity column only the
      // ask-AI catalog offers) still filters: its chip is the AI's, and it is
      // labelled by its own name and values.
      const values = Array.from(selected)
      constraints.push({
        field: colName,
        label: colName,
        display: `${toShortLabel(colName)}: ${summarizeValues(values)}`,
        tooltip: `${colName}: ${values.join(', ')}`,
        values,
        projection: { attribute: colName, values },
      })
    }
    for (const group of groupEntries) {
      if (group.options) {
        const memberSel = groupMemberSelections[group.groupId]
        if (!memberSel) continue
        // Use the shared helper so chip predicate is identical to filter payload
        // — prevents a filter being active with no visible chip (P2 fix).
        const constrained = multiselectGroupConstrainedOptions(group, memberSel)
        if (constrained.length === 0) continue
        const valueSet = new Set<string>()
        for (const opt of constrained) {
          for (const v of memberSel[opt.column]) valueSet.add(v)
        }
        const valueLabels = Array.from(valueSet).map((v) =>
          displaySegmentValueLabel({ valueLabels: group.valueLabels }, v),
        )
        const optionLabels = constrained.map((o) => o.label)
        const allOptions = constrained.length === group.options.length
        const base = toShortLabel(group.label)
        const scope = allOptions ? '' : ` (${constrained.length}/${group.options.length})`
        constraints.push({
          field: group.groupId,
          label: group.label,
          display: `${base}${scope}: ${summarizeValues(valueLabels)}`,
          tooltip:
            `${group.fullLabel}\n` +
            `${allOptions ? 'All options' : optionLabels.join(', ')}\n` +
            `Values: ${valueLabels.join(', ')}`,
          values: Array.from(valueSet),
          projection: {
            attribute: group.fullLabel,
            options: optionLabels,
            optionsMatch: (groupLogic[group.groupId] ?? 'OR') === 'AND' ? 'all' : 'any',
            values: valueLabels,
          },
        })
        continue
      }
      const selected = groupSelections[group.groupId]
      if (!selected || selected.size === 0) continue
      // Skip a fully-selected binary group — no constraint.
      if (selected.size === group.uniqueValues.length) continue
      const valueLabels = Array.from(selected).map((v) =>
        displaySegmentValueLabel({ valueLabels: group.valueLabels }, v),
      )
      constraints.push({
        field: group.groupId,
        label: group.label,
        display: `${toShortLabel(group.label)}: ${summarizeValues(valueLabels)}`,
        tooltip: `${group.fullLabel}: ${valueLabels.join(', ')}`,
        values: Array.from(selected),
        projection: { attribute: group.fullLabel, values: valueLabels },
      })
    }
    return constraints
  }, [columns, filteredColumns, selections, groupEntries, groupSelections, groupMemberSelections, groupLogic, syntheticColNames])

  // A column or group an AI chip picked shows as that chip.
  const activeFilterChips = useMemo(() => {
    const aiChipColumns = new Set(aiChips.map((c) => c.column))
    return labelledConstraints.filter((constraint) => !aiChipColumns.has(constraint.field))
  }, [labelledConstraints, aiChips])

  const displayCount = matchingCount !== null && matchingCount !== undefined ? matchingCount : totalRows
  const percentage = totalRows > 0 ? Math.round((displayCount / totalRows) * 100) : 100
  const isBelowMinimum = displayCount < minPersonas
  const canApplyFilter = (hasActiveManualFilter || aiChips.length > 0 || activeTagIds.size > 0) && !isBelowMinimum && !previewLoading && !tagError

  useEffect(() => {
    if (!open) return

    initialPreviewDoneRef.current = false
    prevGroupEntriesRef.current = null
    setSelections({})
    setGroupSelections({})
    setGroupMemberSelections({})
    setGroupRowSelections({})
    setGroupLogic({})
    setMatchingCount(null)
    setPreviewLoading(false)
    setExpandedCategories(new Set())
    setSearchQuery('')
    setLoadError(false)
    setAiChips(activeAiChips ?? [])
    setTagSizes({})
    setTagError(null)
    setActiveTagIds(new Set())
    setSyntheticColNames(new Set())
    setColumns([])
    setGroups([])
    setTotalRows(0)
    setLoading(true)

    function applyData(data: ColumnMetadataResult) {
      const cols = showOperationalColumns
        ? data.columns
        : data.columns.filter((c: ColumnMeta) => !c.isOperational)
      setColumns(data.columns)
      setGroups(data.groups ?? [])
      setTotalRows(data.totalRows)
      setActiveTagIds(new Set())
      // Restored AI chips become selections once the groups are known (below).
      setSelections(initializeSelections(cols))
      setLoading(false)
    }

    const cacheKey = `${populationId}:${showOperationalColumns}`
    const cached = getCached(cacheKey) ?? prefetchedData ?? null
    if (cached) {
      applyData(cached)
      return
    }

    let cancelled = false
    setLoading(true)

    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        cancelled = true
        setLoading(false)
        setLoadError(true)
      }
    }, 60_000)

    populationsApi.getColumnMetadata(populationId, {
      includeAllColumns: showOperationalColumns,
    }).then((data: ColumnMetadataResult) => {
      clearTimeout(timeoutId)
      if (cancelled) return
      setCached(cacheKey, data)
      applyData(data)
    }).catch(() => {
      clearTimeout(timeoutId)
      if (!cancelled) {
        setLoading(false)
        setLoadError(true)
      }
    })

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [open, populationId, prefetchedData, showOperationalColumns])

  const firePreview = useCallback(
    (filter: PopulationFilter | null) => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
      if (abortRef.current) abortRef.current.abort()

      if (!filter) {
        setMatchingCount(null)
        setPreviewLoading(false)
        return
      }

      setPreviewLoading(true)
      previewTimerRef.current = setTimeout(async () => {
        const controller = new AbortController()
        abortRef.current = controller

        try {
          const previewFn = onPreviewFilter ?? ((f: PopulationFilter) => populationsApi.previewFilter(populationId, f))
          const result = await previewFn(filter)
          if (!controller.signal.aborted) {
            setMatchingCount(result.matchingCount)
            setPreviewLoading(false)
          }
        } catch {
          if (!controller.signal.aborted) {
            setPreviewLoading(false)
          }
        }
      }, PREVIEW_DEBOUNCE_MS)
    },
    [populationId, onPreviewFilter],
  )

  const requestPreview = useCallback(
    (
      currentSelections: Record<string, Set<string>>,
      currentGroupSelections?: Record<string, Set<string>>,
      currentGroupMemberSelections?: Record<string, Record<string, Set<string>>>,
      currentGroupLogic?: Record<string, 'OR' | 'AND'>,
    ) => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
      if (abortRef.current) abortRef.current.abort()

      const resolvedGroupSelections = currentGroupSelections ?? groupSelections
      const resolvedGroupMemberSelections =
        currentGroupMemberSelections ?? groupMemberSelections
      const resolvedGroupLogic = currentGroupLogic ?? groupLogic

      const filter = buildFilterFromSelections(
        currentSelections,
        filteredColumns,
        resolvedGroupSelections,
        groupEntries,
        resolvedGroupMemberSelections,
        resolvedGroupLogic,
        syntheticColNames,
      )

      // One builder for the whole pick (C09-1): AI choices are already
      // selections, so nothing is assembled beside it.
      const combinedFilter = filter

      if (!combinedFilter) {
        setMatchingCount(null)
        setPreviewLoading(false)
        return
      }

      setPreviewLoading(true)
      previewTimerRef.current = setTimeout(async () => {
        const controller = new AbortController()
        abortRef.current = controller

        try {
          const previewFn = onPreviewFilter ?? ((f: PopulationFilter) => populationsApi.previewFilter(populationId, f))
          const result = await previewFn(combinedFilter)
          if (!controller.signal.aborted) {
            setMatchingCount(result.matchingCount)
            setPreviewLoading(false)
          }
        } catch {
          if (!controller.signal.aborted) {
            setPreviewLoading(false)
          }
        }
      }, PREVIEW_DEBOUNCE_MS)
    },
    [populationId, filteredColumns, onPreviewFilter, groupSelections, groupMemberSelections, groupLogic, groupEntries, syntheticColNames],
  )

  const initialPreviewDoneRef = useRef(false)
  const prevGroupEntriesRef = useRef<Array<GroupEntry> | null>(null)

  useEffect(() => {
    if (!open || loading) return
    if (filteredColumns.length === 0) return
    if (initialPreviewDoneRef.current) return

    // An initial filter reopens as the picker's own selections, beside any restored chips.
    const restoredChoices: Array<AskAiFilterChoice> = [
      ...(activeAiChips ?? []),
      ...choicesFromInitialFilter(initialFilter),
    ]
    if (restoredChoices.length > 0) {
      initialPreviewDoneRef.current = true
      // The chips as the picker's own selections, groups included (C09-1).
      const picked = selectionsFromAiChoices(restoredChoices, groupEntries)
      const restored = { ...selections, ...picked.selections }
      const restoredMembers = { ...groupMemberSelections, ...picked.groupMemberSelections }
      setSelections(restored)
      setGroupRowSelections((prev) => ({ ...prev, ...picked.groupRowSelections }))
      setGroupMemberSelections(restoredMembers)
      requestPreview(restored, undefined, restoredMembers)
    }
  }, [open, loading, filteredColumns.length, selections, activeAiChips, initialFilter, requestPreview, groupEntries, groupMemberSelections])

  const toggleValue = useCallback(
    (fieldName: string, value: string) => {
      setSelections((prev) => {
        const current = prev[fieldName]
        if (!current) return prev
        const next = new Set(current)
        if (next.has(value)) {
          next.delete(value)
        } else {
          next.add(value)
        }
        const updated = { ...prev, [fieldName]: next }
        requestPreview(updated)
        return updated
      })
    },
    [requestPreview],
  )

  const selectAll = useCallback(
    (fieldName: string) => {
      const col = filteredColumns.find((c) => c.name === fieldName)
      if (!col) return
      setSelections((prev) => {
        const updated = { ...prev, [fieldName]: new Set(col.uniqueValues) }
        requestPreview(updated)
        return updated
      })
    },
    [filteredColumns, requestPreview],
  )

  const clearAll = useCallback(
    (fieldName: string) => {
      setSelections((prev) => {
        const updated = { ...prev, [fieldName]: new Set<string>() }
        requestPreview(updated)
        return updated
      })
    },
    [requestPreview],
  )

  const applyTag = useCallback(
    (tagId: string) => {
      const tag = normalizedTags?.find((t) => t.id === tagId)
      if (!tag) return

      const prev = activeTagIds
      const next = new Set(prev)
      if (next.has(tagId)) {
        next.delete(tagId)
      } else {
        next.add(tagId)
      }

      setActiveTagIds(next)

      if (forStudy) {
        const tooSmall = (normalizedTags ?? []).find(
          (t) => next.has(t.id) && tagSizes[t.id] !== undefined && tagSizes[t.id] < minPersonas,
        )
        if (tooSmall) {
          setTagError(`Cannot select "${tooSmall.name}" because it contains less than ${minPersonas} respondents`)
        } else {
          setTagError(null)
        }
      } else {
        setTagError(null)
      }

      const activeTags = (normalizedTags ?? []).filter((t) => next.has(t.id))
      const tagColumns = new Set<string>()
      for (const t of activeTags) {
        for (const c of t.conditions) tagColumns.add(c.sourceColumn)
      }

      // Inject synthetic columns for tag conditions that reference columns
      // not in the loaded set so they appear in the manual filter UI.
      const knownNames = new Set(columns.map((c) => c.name))
      const syntheticCols: ColumnMeta[] = []
      for (const t of activeTags) {
        for (const cond of t.conditions) {
          if (knownNames.has(cond.sourceColumn)) continue
          const existing = syntheticCols.find((s) => s.name === cond.sourceColumn)
          if (existing) {
            for (const v of cond.values) {
              if (!existing.uniqueValues.includes(v)) existing.uniqueValues.push(v)
            }
            continue
          }
          const label = cond.sourceColumn
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (l) => l.toUpperCase())
          syntheticCols.push({
            name: cond.sourceColumn,
            displayLabel: label,
            uniqueValues: [...cond.values],
            category: 'other',
            canonicalId: null,
            isOperational: false,
          } as ColumnMeta)
        }
      }
      if (syntheticCols.length > 0) {
        const newSynNames = new Set(syntheticCols.map((c) => c.name))
        setSyntheticColNames((prev) => {
          const merged = new Set([...prev, ...newSynNames])
          return merged.size === prev.size ? prev : merged
        })
        setColumns((prevCols) => {
          const existingNames = new Set(prevCols.map((c) => c.name))
          const toAdd = syntheticCols.filter((c) => !existingNames.has(c.name))
          return toAdd.length > 0 ? [...prevCols, ...toAdd] : prevCols
        })
      }

      // Build column → group map so tag conditions targeting group members
      // update the Options×Values grid, not the single-column selections state.
      const columnToGroup = new Map<string, GroupEntry>()
      for (const ge of groupEntries) {
        if (ge.options) {
          for (const opt of ge.options) {
            columnToGroup.set(opt.column, ge)
          }
        }
      }

      // Collect all groups affected by tags (active + just-deselected) for clearing
      const isDeselecting = next.size < prev.size
      const affectedGroupIds = new Set<string>()
      const allRelevantTags = isDeselecting ? [...activeTags, tag] : activeTags
      for (const t of allRelevantTags) {
        for (const c of t.conditions) {
          const g = columnToGroup.get(c.sourceColumn)
          if (g) affectedGroupIds.add(g.groupId)
        }
      }

      // Compute tag-derived group row selections (clear affected, rebuild from active)
      const newGroupRowSels: Record<string, GroupRowSelection> = {}
      for (const gid of affectedGroupIds) {
        newGroupRowSels[gid] = { options: new Set(), values: new Set() }
      }
      for (const at of activeTags) {
        for (const cond of at.conditions) {
          const group = columnToGroup.get(cond.sourceColumn)
          if (!group) continue
          const rs = newGroupRowSels[group.groupId]
          rs.options.add(cond.sourceColumn)
          for (const v of cond.values) rs.values.add(v)
        }
      }

      // Materialize into per-member value maps
      const newGroupMemSels: Record<string, Record<string, Set<string>>> = {}
      for (const [gid, rs] of Object.entries(newGroupRowSels)) {
        const ge = groupEntries.find((g) => g.groupId === gid)
        if (ge) newGroupMemSels[gid] = materializeGroupMembers(ge, rs)
      }

      if (affectedGroupIds.size > 0) {
        setGroupRowSelections((prevGrs) => ({ ...prevGrs, ...newGroupRowSels }))
        setGroupMemberSelections((prevGms) => ({ ...prevGms, ...newGroupMemSels }))
        setGroupLogic((prevLogic) => {
          const updated = { ...prevLogic }
          for (const gid of affectedGroupIds) updated[gid] = 'AND'
          return updated
        })
      }

      // Update individual column selections
      const updatedSelections = { ...selections }

      // Clear tag-referenced single columns (skip group members)
      for (const colName of tagColumns) {
        if (!columnToGroup.has(colName)) {
          updatedSelections[colName] = new Set<string>()
        }
      }
      // If a tag was just deselected, also clear its columns that no other active tag uses
      if (isDeselecting) {
        const deselectedTag = normalizedTags?.find((t) => t.id === tagId)
        if (deselectedTag) {
          for (const cond of deselectedTag.conditions) {
            if (!tagColumns.has(cond.sourceColumn) && !columnToGroup.has(cond.sourceColumn)) {
              updatedSelections[cond.sourceColumn] = new Set<string>()
            }
          }
        }
      }

      for (const activeTag of activeTags) {
        for (const condition of activeTag.conditions) {
          if (columnToGroup.has(condition.sourceColumn)) continue
          const col = columns.find((c) => c.name === condition.sourceColumn)
          const colName = col?.name ?? condition.sourceColumn
          const existing = updatedSelections[colName] ?? new Set<string>()
          const tagValues = col && col.uniqueValues.length > 0
            ? condition.values.filter((v) => col.uniqueValues.includes(v))
            : condition.values
          for (const v of tagValues) existing.add(v)
          updatedSelections[colName] = existing
        }
      }

      setSelections(updatedSelections)

      const mergedGms = affectedGroupIds.size > 0
        ? { ...groupMemberSelections, ...newGroupMemSels }
        : undefined
      requestPreview(updatedSelections, undefined, mergedGms)

      // Auto-expand categories that contain tag-affected columns/groups
      if (activeTags.length > 0) {
        const categoriesToExpand = new Set<string>()
        for (const t of activeTags) {
          for (const c of t.conditions) {
            const g = columnToGroup.get(c.sourceColumn)
            if (g) {
              categoriesToExpand.add(g.category)
            } else {
              const cat = categorizedColumns.find(([, cols]) =>
                cols.some((col) => col.name === c.sourceColumn),
              )?.[0]
              if (cat) categoriesToExpand.add(cat)
              else categoriesToExpand.add('other')
            }
          }
        }
        if (categoriesToExpand.size > 0) {
          setExpandedCategories((prevExp) => new Set([...prevExp, ...categoriesToExpand]))
        }
      }
    },
    [normalizedTags, columns, filteredColumns, selections, activeTagIds, requestPreview, forStudy, tagSizes, minPersonas, groupEntries, groupMemberSelections, categorizedColumns],
  )


  // Re-sync tag-derived group selections when groupEntries identity changes
  // (e.g. after synthetic columns are injected and groups re-derive).
  useEffect(() => {
    if (!open || activeTagIds.size === 0 || !normalizedTags) return
    if (prevGroupEntriesRef.current === groupEntries) return
    prevGroupEntriesRef.current = groupEntries

    const activeTags = normalizedTags.filter((t) => activeTagIds.has(t.id))
    if (activeTags.length === 0) return
    const columnToGroup = new Map<string, GroupEntry>()
    for (const ge of groupEntries) {
      if (ge.options) {
        for (const opt of ge.options) columnToGroup.set(opt.column, ge)
      }
    }

    const newRowSels: Record<string, GroupRowSelection> = {}
    const affectedGroupIds = new Set<string>()
    for (const tag of activeTags) {
      for (const cond of tag.conditions) {
        const group = columnToGroup.get(cond.sourceColumn)
        if (!group) continue
        affectedGroupIds.add(group.groupId)
        if (!newRowSels[group.groupId]) {
          newRowSels[group.groupId] = { options: new Set(), values: new Set() }
        }
        const rs = newRowSels[group.groupId]
        rs.options.add(cond.sourceColumn)
        for (const v of cond.values) rs.values.add(v)
      }
    }
    if (affectedGroupIds.size === 0) return

    setGroupRowSelections((prev) => ({ ...prev, ...newRowSels }))

    const newGroupMemSels: Record<string, Record<string, Set<string>>> = {}
    for (const [gid, rs] of Object.entries(newRowSels)) {
      const ge = groupEntries.find((g) => g.groupId === gid)
      if (ge) newGroupMemSels[gid] = materializeGroupMembers(ge, rs)
    }
    setGroupMemberSelections((prev) => ({ ...prev, ...newGroupMemSels }))

    setGroupLogic((prev) => {
      const next = { ...prev }
      for (const gid of affectedGroupIds) next[gid] = 'AND'
      return next
    })

    // Clear individual selections for columns now handled by groups,
    // then trigger a preview with the updated state.
    setSelections((prev) => {
      const updated = { ...prev }
      let changed = false
      for (const tag of activeTags) {
        for (const cond of tag.conditions) {
          if (columnToGroup.has(cond.sourceColumn) && updated[cond.sourceColumn]?.size) {
            updated[cond.sourceColumn] = new Set<string>()
            changed = true
          }
        }
      }
      const finalSelections = changed ? updated : prev
      requestPreview(
        finalSelections,
        undefined,
        { ...groupMemberSelections, ...newGroupMemSels },
        { ...groupLogic, ...Object.fromEntries([...affectedGroupIds].map((gid) => [gid, 'AND' as const])) },
      )
      return changed ? updated : prev
    })
  }, [open, activeTagIds, groupEntries, normalizedTags, requestPreview, groupMemberSelections, groupLogic])

  /** Toggle a single value within a group's opt-in selection. */
  const toggleGroupValue = useCallback(
    (groupId: string, value: string) => {
      setGroupSelections((prev) => {
        const current = prev[groupId] ?? new Set<string>()
        const next = new Set(current)
        if (next.has(value)) next.delete(value)
        else next.add(value)
        const updated = { ...prev, [groupId]: next }
        requestPreview(selections, updated)
        return updated
      })
    },
    [requestPreview, selections],
  )

  /**
   * Apply an updated two-row selection for a multiselect group: persist the row
   * state, mirror it into the canonical member-value map, and refresh preview.
   */
  const applyGroupRowSelection = useCallback(
    (group: GroupEntry, nextRow: GroupRowSelection) => {
      setGroupRowSelections((prev) => ({ ...prev, [group.groupId]: nextRow }))
      setGroupMemberSelections((prev) => {
        const members = materializeGroupMembers(group, nextRow)
        const updated = { ...prev, [group.groupId]: members }
        requestPreview(selections, undefined, updated)
        return updated
      })
    },
    [requestPreview, selections],
  )

  /** Toggle an option (member column) in a multiselect group's Options row. */
  const toggleGroupOption = useCallback(
    (group: GroupEntry, column: string) => {
      const cur = groupRowSelections[group.groupId] ?? {
        options: new Set<string>(),
        values: new Set<string>(),
      }
      const options = new Set(cur.options)
      if (options.has(column)) options.delete(column)
      else options.add(column)

      let values = new Set(cur.values)
      // Auto-select the positive value for "Selected / Not selected" groups
      if (
        options.size > 0 &&
        values.size === 0 &&
        isSelectedNotSelectedGroup(group.uniqueValues, group.valueLabels)
      ) {
        values = new Set([resolvePositiveValue(group.uniqueValues, group.valueLabels)])
      }
      applyGroupRowSelection(group, { options, values })
    },
    [groupRowSelections, applyGroupRowSelection],
  )

  /** Toggle a value in a multiselect group's Values row (applies to all options). */
  const toggleGroupOptionValue = useCallback(
    (group: GroupEntry, value: string) => {
      const cur = groupRowSelections[group.groupId] ?? {
        options: new Set<string>(),
        values: new Set<string>(),
      }
      const values = new Set(cur.values)
      if (values.has(value)) values.delete(value)
      else values.add(value)
      applyGroupRowSelection(group, { options: new Set(cur.options), values })
    },
    [groupRowSelections, applyGroupRowSelection],
  )

  /** Clear all selected values for a group (resets to "no filter on this group"). */
  const clearGroupSelection = useCallback(
    (groupId: string) => {
      const group = groupEntries.find((g) => g.groupId === groupId)
      if (group?.options) {
        setGroupRowSelections((prev) => ({
          ...prev,
          [groupId]: { options: new Set<string>(), values: new Set<string>() },
        }))
        setGroupMemberSelections((prev) => {
          const updated = { ...prev, [groupId]: {} }
          requestPreview(selections, groupSelections, updated)
          return updated
        })
      } else {
        setGroupSelections((prev) => {
          const updated = { ...prev, [groupId]: new Set<string>() }
          requestPreview(selections, updated, groupMemberSelections)
          return updated
        })
      }
    },
    [requestPreview, selections, groupEntries, groupSelections, groupMemberSelections],
  )

  /** Switch a multiselect group's internal boolean logic (OR ⇄ AND). */
  const setGroupInternalLogic = useCallback(
    (groupId: string, logic: 'OR' | 'AND') => {
      setGroupLogic((prev) => {
        if ((prev[groupId] ?? 'OR') === logic) return prev
        const updated = { ...prev, [groupId]: logic }
        requestPreview(selections, groupSelections, groupMemberSelections, updated)
        return updated
      })
    },
    [requestPreview, selections, groupSelections, groupMemberSelections],
  )

  const removeChip = useCallback(
    (fieldName: string) => {
      // Regular column chip — clear selection (opt-in model).
      const col = filteredColumns.find((c) => c.name === fieldName)
      if (col) {
        setSelections((prev) => {
          const updated = { ...prev, [fieldName]: new Set<string>() }
          requestPreview(updated)
          return updated
        })
        return
      }
      // Group chip — clear group selection (opt-in model).
      const group = groupEntries.find((g) => g.groupId === fieldName)
      if (group) {
        if (group.options) {
          applyGroupRowSelection(group, {
            options: new Set<string>(),
            values: new Set<string>(),
          })
        } else {
          setGroupSelections((prev) => {
            const updated = { ...prev, [fieldName]: new Set<string>() }
            requestPreview(selections, updated, groupMemberSelections)
            return updated
          })
        }
      }
    },
    [
      filteredColumns,
      requestPreview,
      groupEntries,
      selections,
      groupMemberSelections,
      applyGroupRowSelection,
    ],
  )

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }, [])

  const navigateToField = useCallback((fieldName: string) => {
    // Group chip → expand its category and scroll to the group block.
    let group = groupEntries.find((g) => g.groupId === fieldName)
    if (!group) {
      // Column absorbed into a group — navigate to the parent group instead.
      group = groupEntries.find((g) => g.memberColumns.includes(fieldName))
    }
    if (group) {
      const targetCategory = group.category
      const targetGroupId = group.groupId
      setExpandedCategories((prev) => new Set(prev).add(targetCategory))
      setTimeout(() => {
        const el = document.querySelector(
          `[data-testid="filter-group-${CSS.escape(targetGroupId)}"]`,
        )
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
      return
    }
    const category = categorizedColumns.find(([, cols]) =>
      cols.some((c) => c.name === fieldName),
    )?.[0]
    if (!category) return
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      next.add(category)
      return next
    })
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="filter-field-${CSS.escape(fieldName)}"]`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 50)
  }, [categorizedColumns, groupEntries])

  const handleFilterApply = useCallback(() => {
    const selectionFilter = buildFilterFromSelections(selections, filteredColumns, groupSelections, groupEntries, groupMemberSelections, groupLogic, syntheticColNames)

    let tagNames: Array<string> | undefined
    let selectedSegments: Array<SelectedSegment> | undefined
    if (activeTagIds.size > 0 && normalizedTags) {
      const activeTags = normalizedTags.filter((t) => activeTagIds.has(t.id))
      tagNames = activeTags.map((t) => t.name)
      // The segments travel by id with the conditions applied
      // here, frozen at launch; the names above stay display-only.
      selectedSegments = activeTags.map((t) => ({
        id: t.id,
        label: t.name,
        conditions: t.conditions.map((condition) => ({ ...condition })),
      }))
    }

    // One builder for the whole pick (C09-1): AI chips are selections too.
    const finalFilter: PopulationFilter | null = selectionFilter

    if (!finalFilter) return

    if (onAiApplyFilters) {
      onAiApplyFilters(columnChipFilters(aiChips), aiChips, 'replace')
    }

    onFilterApply(
      finalFilter,
      displayCount,
      tagNames,
      selectedSegments,
      labelledConstraints.map((constraint) => constraint.projection),
    )
  }, [activeTagIds, normalizedTags, selections, filteredColumns, aiChips, displayCount, onFilterApply, onAiApplyFilters, groupSelections, groupMemberSelections, groupLogic, groupEntries, syntheticColNames, labelledConstraints])

  const handleContinueWithout = useCallback(() => {
    onContinueWithout()
  }, [onContinueWithout])

  const currentFiltersForAi = useMemo(() => {
    const filters: Record<string, Array<string>> = {}
    for (const col of filteredColumns) {
      const selected = selections[col.name]
      if (!selected) continue
      if (selected.size < col.uniqueValues.length && selected.size > 0) {
        filters[col.name] = Array.from(selected)
      }
    }
    for (const [column, values] of Object.entries(columnChipFilters(aiChips))) {
      if (!filters[column]) {
        filters[column] = values
      }
    }
    return filters
  }, [filteredColumns, selections, aiChips])

  const handleAiApplyFilters = useCallback(
    (_filters: Record<string, Array<string>>, chips: Array<AiFilterCondition>, mode: 'replace' | 'add') => {
      if (chips.length === 0) return

      let effectiveChips: Array<AiFilterCondition>
      if (mode === 'add') {
        const existingByColumn = new Map(aiChips.map((c) => [c.column, c]))
        const merged: Array<AiFilterCondition> = [...aiChips]
        for (const chip of chips) {
          const existing = existingByColumn.get(chip.column)
          if (existing) {
            const combinedValues = Array.from(new Set([...existing.values, ...chip.values]))
            const combinedOptions = chip.groupId
              ? Array.from(new Set([...(existing.options ?? []), ...(chip.options ?? [])]))
              : undefined
            const mergedChip: AiFilterCondition = {
              ...existing,
              values: combinedValues,
              ...(combinedOptions ? { options: combinedOptions } : {}),
              chip: combinedValues.length > 1 ? `${existing.chip} + ${chip.chip}` : chip.chip,
              displayAnswer: combinedValues.join(', '),
            }
            const idx = merged.findIndex((c) => c.column === chip.column)
            if (idx !== -1) merged[idx] = mergedChip
            existingByColumn.set(chip.column, mergedChip)
          } else {
            merged.push(chip)
            existingByColumn.set(chip.column, chip)
          }
        }
        effectiveChips = merged
        setAiChips(merged)
      } else {
        effectiveChips = chips
        setAiChips(chips)
      }

      // The chips as the picker's own selections (C09-1): a column chip
      // selects its values, a group chip picks its options and values exactly
      // as the group rows do. Replacing drops the previous chips' picks.
      const picked = selectionsFromAiChoices(effectiveChips, groupEntries)
      const replaced = mode === 'replace' ? aiChips : []
      const withoutGroups = <T,>(record: Record<string, T>) =>
        Object.fromEntries(
          Object.entries(record).filter(([key]) => !replaced.some((c) => c.groupId === key)),
        )
      const nextMembers = { ...withoutGroups(groupMemberSelections), ...picked.groupMemberSelections }
      setGroupRowSelections((prev) => ({ ...withoutGroups(prev), ...picked.groupRowSelections }))
      setGroupMemberSelections(nextMembers)
      setSelections((prev) => {
        const updated = mode === 'replace'
          ? Object.fromEntries(Object.entries(prev).filter(([key]) => !replaced.some((c) => !c.groupId && c.column === key)))
          : { ...prev }
        Object.assign(updated, picked.selections)
        requestPreview(updated, undefined, nextMembers)
        return updated
      })

      const categoriesToExpand = new Set<string>()
      for (const chip of chips) {
        const cat = chip.groupId
          ? groupEntries.find((g) => g.groupId === chip.groupId)?.category
          : categorizedColumns.find(([, cols]) => cols.some((c) => c.name === chip.column))?.[0]
        if (cat) categoriesToExpand.add(cat)
      }
      if (categoriesToExpand.size > 0) {
        setExpandedCategories((prev) => new Set([...prev, ...categoriesToExpand]))
      }

      if (onAiApplyFilters) {
        onAiApplyFilters(columnChipFilters(effectiveChips), effectiveChips, mode)
      }
    },
    [categorizedColumns, requestPreview, aiChips, onAiApplyFilters, groupEntries, groupMemberSelections],
  )

  const handleAiFineTune = useCallback(
    (conditions: Array<AiFilterCondition>) => {
      // The AI's choices as the picker's own selections, to edit by hand.
      const picked = selectionsFromAiChoices(conditions, groupEntries)
      const nextMembers = { ...groupMemberSelections, ...picked.groupMemberSelections }
      setGroupRowSelections((prev) => ({ ...prev, ...picked.groupRowSelections }))
      setGroupMemberSelections(nextMembers)
      setSelections((prev) => {
        const updated = { ...prev, ...picked.selections }
        requestPreview(updated, undefined, nextMembers)
        return updated
      })

      setAiChips([])

      const categoriesToExpand = new Set<string>()
      for (const condition of conditions) {
        const cat = condition.groupId
          ? groupEntries.find((g) => g.groupId === condition.groupId)?.category
          : categorizedColumns.find(([, cols]) => cols.some((c) => c.name === condition.column))?.[0]
        if (cat) categoriesToExpand.add(cat)
      }
      if (categoriesToExpand.size > 0) {
        setExpandedCategories((prev) => new Set([...prev, ...categoriesToExpand]))
      }
    },
    [categorizedColumns, requestPreview, groupEntries, groupMemberSelections],
  )

  const handleScopedAiPreview = useCallback(
    async (conditions: AiFilterCondition[]): Promise<{ matchingCount: number; totalCount: number }> => {
      // The picker's filter for these choices, from the one builder (C09-1).
      const picked = selectionsFromAiChoices(conditions, groupEntries)
      const filter = buildFilterFromSelections(
        picked.selections,
        filteredColumns,
        undefined,
        groupEntries,
        picked.groupMemberSelections,
      )
      if (!onPreviewFilter || !filter) {
        return { matchingCount: 0, totalCount: totalRows }
      }
      return onPreviewFilter(filter)
    },
    [onPreviewFilter, totalRows, groupEntries, filteredColumns],
  )

  const handleRemoveAiChip = useCallback(
    (column: string) => {
      const removed = aiChips.find((c) => c.column === column)
      setAiChips(aiChips.filter((c) => c.column !== column))
      if (removed?.groupId) {
        const groupId = removed.groupId
        const nextMembers = Object.fromEntries(
          Object.entries(groupMemberSelections).filter(([key]) => key !== groupId),
        )
        setGroupRowSelections((prev) =>
          Object.fromEntries(Object.entries(prev).filter(([key]) => key !== groupId)),
        )
        setGroupMemberSelections(nextMembers)
        requestPreview(selections, undefined, nextMembers)
        return
      }
      setSelections((prev) => {
        const { [column]: _removed, ...rest } = prev
        const col = filteredColumns.find((c) => c.name === column)
        const updated = col ? { ...rest, [column]: new Set(col.uniqueValues) } : rest
        requestPreview(updated)
        return updated
      })
    },
    [filteredColumns, aiChips, requestPreview, selections, groupMemberSelections],
  )

  useEffect(() => {
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
      if (abortRef.current) abortRef.current.abort()
    }
  }, [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="audience-filter-modal"
        className="max-w-[1080px] h-[85vh] flex flex-col p-0 sm:p-0 gap-0 overflow-hidden"
      >
        {/* Header */}
        <div className="shrink-0 px-6 pt-5 pb-4 space-y-4 overflow-hidden">
          <DialogHeader className="flex flex-row items-start gap-4 space-y-0">
            {coverImageUrl && (
              <img
                src={coverImageUrl}
                alt={populationName}
                className="h-14 w-14 rounded-lg object-cover shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg font-normal">
                {t('creation.filterPopulation')}
              </DialogTitle>
              <DialogDescription className="text-sm mt-1">
                {t('creation.filterPopulationBody')}{' '}
                <span className="font-medium text-foreground">{populationName}</span>
              </DialogDescription>
            </div>
          </DialogHeader>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-center space-y-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">{t('creation.loadingAttributes')}</p>
            </div>
          </div>
        ) : loadError ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-center space-y-4 max-w-sm">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
              <p className="text-sm text-muted-foreground">
                {t('creation.couldNotLoadAttributes')}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const cacheKey = `${populationId}:${showOperationalColumns}`
                  columnMetadataCache.delete(cacheKey)
                  setLoadError(false)
                  setLoading(true)
                  populationsApi.getColumnMetadata(populationId, {
                    includeAllColumns: showOperationalColumns,
                  }).then((data: ColumnMetadataResult) => {
                    setCached(cacheKey, data)
                    const cols = showOperationalColumns
                      ? data.columns
                      : data.columns.filter((c: ColumnMeta) => !c.isOperational)
                    setColumns(data.columns)
                    setGroups(data.groups ?? [])
                    setTotalRows(data.totalRows)
                    setGroupSelections({})
                    setGroupMemberSelections({})
                    setGroupRowSelections({})
                    setSelections(initializeSelections(cols))
                    setLoading(false)
                  }).catch(() => {
                    setLoading(false)
                    setLoadError(true)
                  })
                }}
              >
                {t('audienceCreate.retry')}
              </Button>
            </div>
          </div>
        ) : filteredColumns.length === 0 && groupEntries.length === 0 ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-center space-y-3 max-w-sm">
              <Users className="h-8 w-8 text-muted-foreground/60 mx-auto" />
              <p className="text-sm text-muted-foreground">
                {t('creation.noSegmentAttributes')}
              </p>
            </div>
          </div>
        ) : (
          /* Unified scrollable body — AI filter + manual categories scroll together */
          <div className="flex-1 min-h-0 overflow-hidden">
            <ScrollArea className="h-full [&>[data-radix-scroll-area-viewport]]:!overflow-x-hidden [&>[data-radix-scroll-area-viewport]>div]:!block [&>[data-radix-scroll-area-viewport]>div]:!min-w-0">
              <div className="px-6 pt-2 pb-4 space-y-4">
                {/* AI Filter */}
                <InlineAskAiFilter
                  populationId={populationId}
                  currentFilters={currentFiltersForAi}
                  onApplyFilters={handleAiApplyFilters}
                  onOpenFilterModal={handleAiFineTune}
                  totalPersonas={totalRows}
                  audienceType={audienceType}
                  onScopedPreview={onPreviewFilter ? handleScopedAiPreview : undefined}
                />

                {/* OR divider */}
                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">{t('creation.orBrowseManually')}</span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {/* User Segments (audience tags) */}
                {normalizedTags && normalizedTags.length > 0 && (
                  <div className="space-y-2" data-testid="user-segments-section">
                    <div className="px-1">
                      <span className="text-sm font-medium text-foreground">{t('creation.filterBySubsegments')}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {normalizedTags.map((tag) => {
                        const isActive = activeTagIds.has(tag.id)
                        return (
                          <button
                            key={tag.id}
                            data-testid={`segment-tag-${tag.id}`}
                            onClick={() => applyTag(tag.id)}
                            className={cn(
                              'rounded-full px-4 py-1.5 text-base font-medium leading-none transition-colors',
                              isActive
                                ? 'bg-primary/25 ring-1 ring-primary/40 text-foreground'
                                : 'bg-primary/15 text-foreground hover:bg-primary/20',
                            )}
                          >
                            {tag.name}
                          </button>
                        )
                      })}
                    </div>
                    {tagError && (
                      <p className="flex items-center gap-1.5 text-xs text-destructive px-1">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        {tagError}
                      </p>
                    )}
                    {activeTagIds.size > 0 && !tagError && (
                      <p className="text-xs text-muted-foreground px-1">
                        {activeTagIds.size === 1
                          ? t('creation.filtersSetForSegment')
                          : t('creation.filtersSetForSegments')}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                <div className="relative border-b border-border/60">
                  <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                  <input
                    data-testid="audience-filter-search"
                    placeholder={t('creation.searchAttributes')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full h-9 pl-6 pr-3 text-sm bg-transparent border-none placeholder:text-muted-foreground/40 focus:outline-none"
                  />
                </div>
                {searchFilteredCategories.map(({ category, columns: cols, groups: sectionGroups }) => {
                  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other
                  const isExpanded = expandedCategories.has(category)
                  const totalItems = cols.length + sectionGroups.length

                  return (
                    <div
                      key={category}
                      className="rounded-xl border border-border/30 overflow-hidden w-full"
                      data-testid={`filter-category-${category}`}
                    >
                      {/* Category header */}
                      <button
                        onClick={() => toggleCategory(category)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-3 text-left transition-colors',
                          'hover:bg-accent/30 dark:hover:bg-accent/15',
                          isExpanded && 'border-b border-border/30',
                        )}
                      >
                        <div className="h-8 w-8 flex items-center justify-center rounded-lg border border-border/30 bg-background text-muted-foreground shrink-0">
                          {config.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-normal text-[20px] text-foreground font-display">{t(config.labelKey)}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {t('creation.attributeCount', { count: totalItems })}
                          </span>
                        </div>
                        <span className="flex shrink-0 items-center justify-center size-6 rounded-full bg-primary/15 text-primary transition-transform duration-200">
                          <Plus
                            strokeWidth={1.5}
                            className={cn(
                              'h-3.5 w-3.5 transition-transform duration-200',
                              isExpanded && 'rotate-45',
                            )}
                          />
                        </span>
                      </button>

                      {/* Expanded category content */}
                      {isExpanded && (
                        <div>
                          {/* Group entries (opt-in model) */}
                          {sectionGroups.map((group) => {
                            // Multiselect group → two multi-select rows
                            // (Options × Values). Selecting options + values
                            // expands to per-option CNF filters (see
                            // materializeGroupMembers); backend payload unchanged.
                            if (group.options) {
                              const rowSel = groupRowSelections[group.groupId] ?? {
                                options: new Set<string>(),
                                values: new Set<string>(),
                              }
                              return (
                                <div
                                  key={group.groupId}
                                  className="border-b border-border/30 last:border-b-0 px-4 py-3 overflow-hidden"
                                  data-testid={`filter-group-${group.groupId}`}
                                >
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <HoverLabel
                                        tooltip={group.fullLabel}
                                        className="text-sm font-semibold text-foreground truncate"
                                      />
                                    </div>
                                    <div className="flex items-center gap-3 text-xs shrink-0">
                                      <button
                                        data-testid={`filter-group-select-all-${group.groupId}`}
                                        onClick={() => {
                                          const hideValues = isSelectedNotSelectedGroup(group.uniqueValues, group.valueLabels)
                                          applyGroupRowSelection(group, {
                                            options: new Set(
                                              (group.options ?? []).map((o) => o.column),
                                            ),
                                            values: hideValues
                                              ? new Set([resolvePositiveValue(group.uniqueValues, group.valueLabels)])
                                              : new Set(group.uniqueValues),
                                          })
                                        }}
                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                      >
                                        {t('creation.filterSelectAll')}
                                      </button>
                                      <button
                                        data-testid={`filter-group-clear-${group.groupId}`}
                                        onClick={() => clearGroupSelection(group.groupId)}
                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                      >
                                        {t('creation.filterClearAll')}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex flex-col gap-4">
                                    {/* Options row (Dimension 1) */}
                                    <div
                                      data-testid={`filter-group-options-${group.groupId}`}
                                    >
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-[13px] font-medium text-foreground/80">
                                          {t('creation.filterOptions')}
                                        </span>
                                        {/* Per-group boolean logic: how the
                                            selected options combine. Any = OR
                                            (match at least one), All = AND
                                            (match every selected option). */}
                                        <div
                                          className="flex items-center rounded-md border border-border/60 p-0.5 text-[11px]"
                                          data-testid={`filter-group-logic-${group.groupId}`}
                                          role="group"
                                          aria-label={t('creation.matchLogicAria')}
                                        >
                                          {(['OR', 'AND'] as const).map((mode) => {
                                            const active =
                                              (groupLogic[group.groupId] ?? 'OR') === mode
                                            return (
                                              <button
                                                key={mode}
                                                type="button"
                                                data-testid={`filter-group-logic-${mode.toLowerCase()}-${group.groupId}`}
                                                aria-pressed={active}
                                                title={
                                                  mode === 'OR'
                                                    ? t('creation.matchLogicOrTitle')
                                                    : t('creation.matchLogicAndTitle')
                                                }
                                                onClick={() =>
                                                  setGroupInternalLogic(group.groupId, mode)
                                                }
                                                className={cn(
                                                  'px-2 py-0.5 rounded transition-colors font-medium',
                                                  active
                                                    ? 'bg-primary/15 text-foreground dark:bg-primary/20'
                                                    : 'text-muted-foreground hover:text-foreground',
                                                )}
                                              >
                                                {mode === 'OR' ? t('creation.filterAny') : t('creation.filterAll')}
                                              </button>
                                            )
                                          })}
                                        </div>
                                      </div>
                                      <div className="flex flex-wrap gap-2 w-full">
                                        {group.options.map((opt) => {
                                          const isChecked = rowSel.options.has(opt.column)
                                          return (
                                            <label
                                              key={opt.column}
                                              className={filterOptionPillClass(isChecked, 'max-w-full')}
                                            >
                                              <Checkbox
                                                data-testid={`filter-group-option-${group.groupId}-${opt.column}`}
                                                checked={isChecked}
                                                onCheckedChange={() => toggleGroupOption(group, opt.column)}
                                                className={FILTER_OPTION_CHECKBOX_CLASS}
                                              />
                                              <span
                                                title={opt.label}
                                                className="text-[14px] text-foreground leading-none min-w-0 truncate"
                                              >
                                                {opt.label}
                                              </span>
                                            </label>
                                          )
                                        })}
                                      </div>
                                    </div>

                                    {/* Values row (Dimension 2) — hidden for binary Selected/Not selected groups */}
                                    {!isSelectedNotSelectedGroup(group.uniqueValues, group.valueLabels) && (
                                    <div
                                      data-testid={`filter-group-values-${group.groupId}`}
                                    >
                                      <span className="block text-[13px] font-medium text-foreground/80 mb-1.5">
                                        {t('creation.filterValues')}
                                      </span>
                                      <div className="flex flex-wrap gap-2 w-full">
                                        {group.uniqueValues.map((value) => {
                                          const isChecked = rowSel.values.has(value)
                                          return (
                                            <label
                                              key={value}
                                              className={filterOptionPillClass(isChecked, 'max-w-full')}
                                            >
                                              <Checkbox
                                                data-testid={`filter-group-value-${group.groupId}-${value}`}
                                                checked={isChecked}
                                                onCheckedChange={() => toggleGroupOptionValue(group, value)}
                                                className={FILTER_OPTION_CHECKBOX_CLASS}
                                              />
                                              <span className="text-[14px] text-foreground leading-none min-w-0 truncate">
                                                {displaySegmentValueLabel(
                                                  { valueLabels: group.valueLabels },
                                                  value,
                                                )}
                                              </span>
                                            </label>
                                          )
                                        })}
                                      </div>
                                    </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }

                            // 2D binary: question → aggregated values (match any member).
                            const selected = groupSelections[group.groupId] ?? new Set<string>()
                            const allSelected = selected.size === group.uniqueValues.length

                            return (
                              <div
                                key={group.groupId}
                                className="border-b border-border/30 last:border-b-0 px-4 py-3 overflow-hidden"
                                data-testid={`filter-group-${group.groupId}`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex flex-col min-w-0">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <HoverLabel
                                        tooltip={group.fullLabel}
                                        className="text-sm font-semibold text-foreground truncate"
                                      />
                                      <Badge
                                        variant="outline"
                                        className="text-[10px] font-normal text-muted-foreground px-1.5 py-0 shrink-0"
                                      >
                                        {group.memberColumns.length} items
                                      </Badge>
                                    </div>
                                    {isDisplayableCode(group.groupId, group.fullLabel) && (
                                      <span
                                        data-testid={`filter-group-code-${group.groupId}`}
                                        className="text-[11px] text-muted-foreground/50 font-mono truncate"
                                      >
                                        {group.groupId}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs shrink-0">
                                    <button
                                      data-testid={`filter-group-select-all-${group.groupId}`}
                                      onClick={() => {
                                        const all = new Set(group.uniqueValues)
                                        setGroupSelections((prev) => {
                                          const updated = { ...prev, [group.groupId]: all }
                                          requestPreview(selections, updated)
                                          return updated
                                        })
                                      }}
                                      className={cn(
                                        'text-muted-foreground hover:text-foreground transition-colors',
                                        allSelected && 'text-foreground font-medium',
                                      )}
                                    >
                                      {t('creation.filterSelectAll')}
                                    </button>
                                    <button
                                      data-testid={`filter-group-clear-${group.groupId}`}
                                      onClick={() => clearGroupSelection(group.groupId)}
                                      className="text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                      {t('creation.filterClearAll')}
                                    </button>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2 w-full">
                                  {group.uniqueValues.map((value) => {
                                    const isChecked = selected.has(value)
                                    return (
                                      <label
                                        key={value}
                                        className={filterOptionPillClass(isChecked, 'max-w-full')}
                                      >
                                        <Checkbox
                                          data-testid={`filter-group-checkbox-${group.groupId}-${value}`}
                                          checked={isChecked}
                                          onCheckedChange={() => toggleGroupValue(group.groupId, value)}
                                          className={FILTER_OPTION_CHECKBOX_CLASS}
                                        />
                                        <span className="text-[14px] text-foreground leading-none min-w-0 truncate">
                                          {displaySegmentValueLabel(
                                            { valueLabels: group.valueLabels },
                                            value,
                                          )}
                                        </span>
                                      </label>
                                    )
                                  })}
                                </div>
                              </div>
                            )
                          })}

                          {/* Individual column entries (opt-in model) */}
                          {cols.map((col) => {
                            const selected = selections[col.name] || new Set<string>()
                            const allSelected = selected.size === col.uniqueValues.length
                            const fieldLabel = resolveFieldLabel(col)

                            return (
                              <div
                                key={col.name}
                                className="border-b border-border/30 last:border-b-0 px-4 py-3 overflow-hidden"
                                data-testid={`filter-field-${col.name}`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex flex-col min-w-0">
                                    <HoverLabel
                                      tooltip={fieldLabel}
                                      className="text-sm font-semibold text-foreground truncate"
                                    />
                                    {isDisplayableCode(col.name, fieldLabel) && (
                                      <span
                                        data-testid={`filter-field-code-${col.name}`}
                                        className="text-[11px] text-muted-foreground/50 font-mono truncate"
                                      >
                                        {col.name}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs shrink-0">
                                    <button
                                      data-testid={`filter-select-all-${col.name}`}
                                      onClick={() => selectAll(col.name)}
                                      className={cn(
                                        'text-muted-foreground hover:text-foreground transition-colors',
                                        allSelected && 'text-foreground font-medium',
                                      )}
                                    >
                                      {t('creation.filterSelectAll')}
                                    </button>
                                    <button
                                      data-testid={`filter-clear-all-${col.name}`}
                                      onClick={() => clearAll(col.name)}
                                      className="text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                      {t('creation.filterClearAll')}
                                    </button>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2 w-full">
                                  {col.uniqueValues.map((value) => {
                                    const isChecked = selected.has(value)
                                    return (
                                      <label
                                        key={value}
                                        className={filterOptionPillClass(isChecked, 'max-w-full')}
                                      >
                                        <Checkbox
                                          data-testid={`filter-checkbox-${col.name}-${value}`}
                                          checked={isChecked}
                                          onCheckedChange={() => toggleValue(col.name, value)}
                                          className={FILTER_OPTION_CHECKBOX_CLASS}
                                        />
                                        <span className="text-[14px] text-foreground leading-none min-w-0 truncate">
                                          {displaySegmentValueLabel(col, value)}
                                        </span>
                                      </label>
                                    )
                                  })}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Footer */}
        <div className="shrink-0 px-6 py-5 bg-muted/60 dark:bg-muted/40 space-y-4 rounded-b-[inherit]">
          {/* Active filter chips — AI and manual in one row */}
          {(aiChips.length > 0 || activeFilterChips.length > 0) && (
            <div className="flex items-center gap-2 flex-wrap">
              {aiChips.map((chip) => (
                <div
                  key={chip.column}
                  className="group/chip relative flex items-start gap-1.5 rounded-lg border border-border/40 dark:border-white/10 bg-background dark:bg-[hsl(var(--card-inset))] px-2.5 py-1 text-sm"
                >
                  <div className="min-w-0 flex-1 flex items-center gap-1">
                    <Sparkles className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="font-medium text-foreground">{chip.chip}</span>
                  </div>
                  <button
                    type="button"
                    data-testid={`ai-chip-remove-${chip.column}`}
                    onClick={() => handleRemoveAiChip(chip.column)}
                    className="mt-0.5 shrink-0 hover:bg-foreground/10 dark:hover:bg-white/10 rounded p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  {chip.displayQuestion && (
                    <div className="pointer-events-none absolute bottom-full left-0 mb-1.5 px-2 py-1 rounded bg-popover border border-border shadow-md text-xs text-popover-foreground max-w-[260px] whitespace-normal opacity-0 group-hover/chip:opacity-100 transition-opacity z-[60]">
                      {chip.displayQuestion}{chip.displayAnswer ? `: ${chip.displayAnswer}` : ''}
                    </div>
                  )}
                </div>
              ))}
              {activeFilterChips.map((chip) => (
                <Badge
                  key={chip.field}
                  variant="secondary"
                  className="gap-0 pr-1 font-normal text-[14px] bg-background dark:bg-[hsl(var(--card-inset))] dark:text-white dark:border-0 dark:hover:bg-[hsl(var(--card-inset))]/80 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => navigateToField(chip.field)}
                    title={chip.tooltip}
                    className="px-2 py-0.5 hover:opacity-70 transition-opacity truncate min-w-0 max-w-[280px]"
                  >
                    {chip.display}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeChip(chip.field)}
                    className="hover:bg-foreground/10 rounded p-0.5 ml-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Persona count + progress bar */}
          {!loading && (filteredColumns.length > 0 || groupEntries.length > 0) && (
            <div className="space-y-2">
              <div className="flex items-end justify-between">
                <div>
                  {previewLoading ? (
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                      Calculating...
                    </span>
                  ) : (
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-display font-normal text-[28px] leading-none text-primary">
                        {displayCount.toLocaleString()}
                      </span>
                      <span className="text-sm text-muted-foreground dark:text-white/50">
                        / {totalRows.toLocaleString()} personas
                      </span>
                    </div>
                  )}
                  {isBelowMinimum && (
                    <p
                      data-testid="audience-filter-minimum-warning"
                      className="flex items-center gap-1.5 text-xs text-destructive dark:text-red-300 mt-1"
                    >
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      Minimum of {minPersonas} personas needed
                    </p>
                  )}
                </div>
                <span
                  data-testid="audience-filter-percentage"
                  className={cn(
                    'text-sm font-medium tabular-nums',
                    isBelowMinimum
                      ? 'text-destructive dark:text-red-400'
                      : 'text-primary',
                  )}
                >
                  {percentage}%
                </span>
              </div>

              <div className="h-1 bg-foreground/10 dark:bg-white/10 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full transition-all duration-500 ease-out rounded-full',
                    isBelowMinimum ? 'bg-destructive' : 'bg-[hsl(var(--progress-bar-accent))]',
                  )}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-3">
            {!loading && filteredColumns.length > 0 && (
              <Button
                data-testid="audience-filter-apply-button"
                onClick={handleFilterApply}
                disabled={!canApplyFilter}
                variant="cta"
                className="h-10 rounded-lg px-4 text-base font-medium"
              >
                {t('creation.filterPopulation')}
              </Button>
            )}
            <Button
              data-testid="audience-filter-continue-button"
              onClick={handleContinueWithout}
              variant="secondary"
              className="h-10 rounded-lg px-4 text-base font-medium"
            >
              {t('creation.continueWithoutFiltering')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
